// React Query hooks for the skip-jobs surface.
//
// Pairs with /api/v1/skip/jobs/* — list, detail, create, update, plus
// the workflow actions (collection-request / exchange-request /
// tip-return-request / postpone / complete-collection /
// complete-exchange / complete-tip-return) added in §7.1.
//
// All workflow actions invalidate both the list and the affected detail
// keys so the table + detail panel reflect the new status without a
// manual refetch.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import type {
  AvailableJobAction,
  CustomerSite,
  SkipJob,
  SkipJobAction,
  SkipJobListResponse,
  SkipSize,
} from '../../../types/api';

interface SkipJobsFilters {
  page?: number;
  limit?: number;
  status?: string;
  driver_uuid?: string;
  customer_uuid?: string;
  skip_inventory_uuid?: string;
  date_from?: string;
  date_to?: string;
  q?: string;
}

export function useSkipJobs(filters: SkipJobsFilters = {}) {
  return useQuery<SkipJobListResponse>({
    queryKey: queryKeys.skipJobs.list(filters as Record<string, unknown>),
    queryFn: async () => {
      const res = await apiClient.get<SkipJobListResponse>(endpoints.skipJobs.list, {
        params: filters,
      });
      return res.data;
    },
    staleTime: 30_000,
  });
}

export function useSkipJob(encId: string | null | undefined) {
  return useQuery<{ job: SkipJob }>({
    queryKey: queryKeys.skipJobs.detail(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useSkipJob called without enc_id');
      const res = await apiClient.get<{ job: SkipJob }>(endpoints.skipJobs.detail(encId));
      return res.data;
    },
    enabled: !!encId,
    staleTime: 30_000,
  });
}

interface ActiveActionsFilters {
  page?: number;
  limit?: number;
  q?: string;
}

/**
 * Lists skip jobs with any sub-flag (collection / exchange / tip-return)
 * in 'requested' state. Server-side endpoint
 * GET /api/v1/skip/jobs/active-actions sorts is_priority DESC first.
 * Client-side search (job_number / customer_name / postcode) filters
 * the loaded page locally — the list is naturally bounded.
 */
export function useActiveActionJobs(filters: ActiveActionsFilters = {}) {
  return useQuery<SkipJobListResponse>({
    queryKey: queryKeys.skipJobs.activeActions(filters as Record<string, unknown>),
    queryFn: async () => {
      const res = await apiClient.get<SkipJobListResponse>(
        endpoints.skipJobs.activeActions,
        { params: filters },
      );
      return res.data;
    },
    staleTime: 15_000,
  });
}

/**
 * PATCH /skip/jobs/:enc_id/priority — toggle is_priority + (optional)
 * priority_notes. Invalidates the detail + active-actions caches so
 * the badge + sort flips reflect immediately.
 */
export function useUpdateSkipJobPriority(encId: string) {
  const qc = useQueryClient();
  return useMutation<
    { job: SkipJob },
    Error,
    { is_priority: boolean; priority_notes?: string | null }
  >({
    mutationFn: async (body) => {
      const res = await apiClient.patch<
        { job: SkipJob },
        { is_priority: boolean; priority_notes?: string | null }
      >(endpoints.skipJobs.priority(encId), body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.detail(encId) });
    },
  });
}

/**
 * Phase 2b — dynamic action discovery for SkipJobDetailPage.
 * GET /skip/jobs/:enc_id/available-actions returns the action list
 * for the job's current (status, sub_flags) tuple. Refetches on
 * window focus so action buttons stay in sync after mutations.
 */
export function useAvailableActions(encId: string | null | undefined) {
  return useQuery<{ actions: AvailableJobAction[] }>({
    queryKey: queryKeys.skipJobs.availableActions(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useAvailableActions called without enc_id');
      const res = await apiClient.get<{ actions: AvailableJobAction[] }>(
        endpoints.skipJobs.availableActions(encId),
      );
      return res.data;
    },
    enabled: !!encId,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Lists skip jobs whose `parent_job_id` matches the given parent — i.e.
 * the child jobs spawned by complete-exchange / complete-tip-return.
 * Powered by the §7-style `parent_job_uuid` filter on `/skip/jobs`.
 *
 * The argument is the parent job's raw uuid (`SkipJob.uuid`), not the
 * encrypted url id — the server resolves it via `findJobByUuidScoped`
 * and returns an empty list when the uuid lives in another tenant.
 */
export function useChildSkipJobs(parentUuid: string | null | undefined) {
  return useQuery<SkipJobListResponse>({
    queryKey: queryKeys.skipJobs.children(parentUuid ?? ''),
    queryFn: async () => {
      if (!parentUuid) throw new Error('useChildSkipJobs called without parent uuid');
      const res = await apiClient.get<SkipJobListResponse>(endpoints.skipJobs.list, {
        params: { parent_job_uuid: parentUuid },
      });
      return res.data;
    },
    enabled: !!parentUuid,
    staleTime: 30_000,
  });
}

export function useCreateSkipJob() {
  const qc = useQueryClient();
  return useMutation<{ job: SkipJob }, Error, Record<string, unknown>>({
    mutationFn: async (body) => {
      const res = await apiClient.post<{ job: SkipJob }, Record<string, unknown>>(
        endpoints.skipJobs.create,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.all });
    },
  });
}

export function useUpdateSkipJob(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ job: SkipJob }, Error, Record<string, unknown>>({
    mutationFn: async (body) => {
      const res = await apiClient.put<{ job: SkipJob }, Record<string, unknown>>(
        endpoints.skipJobs.update(encId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.detail(encId) });
    },
  });
}

export interface SkipJobActionPayload {
  notes?: string;
  new_date?: string;
  reason?: string;
  new_skip_inventory_uuid?: string;
  new_skip_size_id?: number;
}

/**
 * Generic workflow-action mutation. The `action` is one of the seven
 * verbs from §7.1. The payload shape varies by action — postpone needs
 * `new_date`, complete-exchange needs `new_skip_inventory_uuid`, etc.
 */
export function useSkipJobAction(encId: string) {
  const qc = useQueryClient();
  return useMutation<
    { job?: SkipJob; original?: SkipJob; child?: SkipJob },
    Error,
    { action: SkipJobAction; payload?: SkipJobActionPayload }
  >({
    mutationFn: async ({ action, payload }) => {
      const res = await apiClient.post<
        { job?: SkipJob; original?: SkipJob; child?: SkipJob },
        SkipJobActionPayload
      >(endpoints.skipJobs.action(encId, action), payload ?? {});
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.detail(encId) });
      void qc.invalidateQueries({ queryKey: queryKeys.skipInventory.all });
    },
  });
}

/**
 * Feature 2 — GET /skip/enquiries/pending-requests. Returns rows
 * from skip_jobs (NOT skip_enquiries) where any sub-flag is
 * 'requested'. Used by the SkipEnquiriesListPage's "Pending
 * Requests" tab. Lives in useSkipJobs.ts because the row shape is
 * SkipJobSummary; the URL routes through the enquiries module by
 * design (the tab visually belongs to the enquiries page).
 */
export function usePendingRequests(filters: { q?: string; page?: number; limit?: number } = {}) {
  return useQuery<SkipJobListResponse>({
    queryKey: queryKeys.skipEnquiries.pendingRequests(filters as Record<string, unknown>),
    queryFn: async () => {
      const res = await apiClient.get<SkipJobListResponse>(
        endpoints.skipEnquiries.pendingRequests,
        { params: filters },
      );
      return res.data;
    },
    staleTime: 15_000,
  });
}

/**
 * Feature 1 — POST /skip/jobs/:enc_id/revert-status. Walks the audit
 * log to find the previous status (most recent `status_changed`
 * activity row's `old_value`) and rolls the job back. Refused
 * server-side for jobs in collected/completed/cancelled.
 */
export function useRevertJobStatus(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ job: SkipJob }, Error, void>({
    mutationFn: async () => {
      const res = await apiClient.post<{ job: SkipJob }, Record<string, never>>(
        endpoints.skipJobs.revertStatus(encId),
        {},
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.detail(encId) });
      void qc.invalidateQueries({
        queryKey: queryKeys.skipJobs.availableActions(encId),
      });
    },
  });
}

/**
 * Status PATCH (state-machine transitions, e.g. delivered → collected,
 * collected → completed). Separate from useSkipJobAction because it hits
 * the /:enc_id/status sub-resource and uses PATCH not POST.
 */
export function useSkipJobStatusChange(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ job: SkipJob }, Error, { new_status: string; notes?: string }>({
    mutationFn: async (body) => {
      const res = await apiClient.patch<{ job: SkipJob }, { new_status: string; notes?: string }>(
        endpoints.skipJobs.status(encId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.detail(encId) });
    },
  });
}

// -- Reference data ---------------------------------------------------------

export function useSkipSizes() {
  return useQuery<{ skipSizes: SkipSize[] }>({
    queryKey: queryKeys.skipSizes.list,
    queryFn: async () => {
      const res = await apiClient.get<{ skipSizes: SkipSize[] }>(endpoints.skipSizes.list);
      return res.data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useCustomerSites(
  customerEncId: string | null | undefined,
  opts: { includeInactive?: boolean } = {},
) {
  const includeInactive = opts.includeInactive ?? false;
  return useQuery<{ sites: CustomerSite[] }>({
    queryKey: [
      ...queryKeys.customerSites.list(customerEncId ?? ''),
      { includeInactive },
    ],
    queryFn: async () => {
      if (!customerEncId) throw new Error('useCustomerSites called without customer id');
      const res = await apiClient.get<{ sites: CustomerSite[] }>(
        endpoints.customerSites.list(customerEncId),
        includeInactive ? { params: { include_inactive: '1' } } : undefined,
      );
      return res.data;
    },
    enabled: !!customerEncId,
    staleTime: 60_000,
  });
}

export interface CreateCustomerSiteInput {
  address_line1: string;
  address_line2?: string | null;
  city?: string | null;
  postcode: string;
  label?: string;
}

/**
 * POST /api/v1/customers/:enc_id/sites — used by the SitePicker's
 * inline "Add new site" sub-form. Invalidates the customer's site
 * list on success so the new row appears in the dropdown.
 */
export function useCreateCustomerSite(customerEncId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation<{ site: CustomerSite }, Error, CreateCustomerSiteInput>({
    mutationFn: async (body) => {
      if (!customerEncId) throw new Error('useCreateCustomerSite called without customer id');
      const res = await apiClient.post<{ site: CustomerSite }, CreateCustomerSiteInput>(
        endpoints.customerSites.create(customerEncId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      if (customerEncId) {
        void qc.invalidateQueries({
          queryKey: queryKeys.customerSites.list(customerEncId),
        });
      }
    },
  });
}

export interface UpdateCustomerSiteInput {
  address_line1?: string;
  address_line2?: string | null;
  city?: string | null;
  postcode?: string;
  label?: string;
  is_active?: boolean;
  /** Promote-only — server demotes other sites for this customer in
   *  the same transaction. Pass `false` is meaningless (a customer
   *  must always have a primary), so the server only accepts `true`. */
  is_primary?: true;
}

/**
 * PATCH /api/v1/customers/:enc_id/sites/:site_enc_id — used by the
 * SkipCustomerDetailPage Sites section. Invalidates both the
 * include-inactive AND active-only site lists for the customer.
 */
export function useUpdateCustomerSite(customerEncId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation<
    { site: CustomerSite },
    Error,
    { siteEncId: string; patch: UpdateCustomerSiteInput }
  >({
    mutationFn: async ({ siteEncId, patch }) => {
      if (!customerEncId) {
        throw new Error('useUpdateCustomerSite called without customer id');
      }
      const res = await apiClient.patch<{ site: CustomerSite }, UpdateCustomerSiteInput>(
        endpoints.customerSites.update(customerEncId, siteEncId),
        patch,
      );
      return res.data;
    },
    onSuccess: () => {
      if (customerEncId) {
        void qc.invalidateQueries({
          queryKey: queryKeys.customerSites.list(customerEncId),
        });
      }
    },
  });
}

/**
 * DELETE /api/v1/customers/:enc_id/sites/:site_enc_id — soft-deletes
 * the site. Server returns 409 when the site has accumulated job
 * history; the caller should surface the error message inline.
 */
export function useDeleteCustomerSite(customerEncId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation<void, Error, { siteEncId: string }>({
    mutationFn: async ({ siteEncId }) => {
      if (!customerEncId) {
        throw new Error('useDeleteCustomerSite called without customer id');
      }
      await apiClient.delete(
        endpoints.customerSites.remove(customerEncId, siteEncId),
      );
    },
    onSuccess: () => {
      if (customerEncId) {
        void qc.invalidateQueries({
          queryKey: queryKeys.customerSites.list(customerEncId),
        });
      }
    },
  });
}
